import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage } from '@/lib/api';

const schema = z.object({ email: z.email('Enter a valid email address') });
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async ({ email }) => {
    setError(null);
    try {
      await api.auth.forgotPassword(email);
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <AuthLayout demo={false}
      title="Reset your password"
      description="Enter your email and we will send you a link to choose a new password."
      footer={
        <Link to="/login" className="hover:text-foreground underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <Alert>
          <AlertTitle>Check your email</AlertTitle>
          <AlertDescription>If an account exists for that address, a reset link is on its way. The link expires in one hour.</AlertDescription>
        </Alert>
      ) : (
        <form onSubmit={onSubmit} className="grid gap-5" noValidate>
          <FormError message={error} />
          <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input id="email" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus placeholder="you@company.com" aria-invalid={!!form.formState.errors.email} {...form.register('email')} />
          </Field>
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
